import React, { useEffect, useState } from 'react';
import { getAllSliderImages, createSliderImage, updateSliderImage, deleteSliderImage, uploadImage } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Upload, Loader2, GripVertical, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function SliderManagement() {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    image_url: '',
    title: '',
    subtitle: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const response = await getAllSliderImages();
      setSliders(response.data);
    } catch (error) {
      toast.error('Slider görselleri yüklenirken hata');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await uploadImage(file);
      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Görsel yüklendi');
    } catch (error) {
      toast.error('Görsel yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        order: parseInt(formData.order) || 0
      };
      
      if (editingSlider) {
        await updateSliderImage(editingSlider.id, data);
        toast.success('Slider güncellendi');
      } else {
        await createSliderImage(data);
        toast.success('Slider eklendi');
      }
      setIsModalOpen(false);
      resetForm();
      fetchSliders();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider);
    setFormData({
      image_url: slider.image_url,
      title: slider.title,
      subtitle: slider.subtitle,
      order: slider.order,
      is_active: slider.is_active
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu slider görselini silmek istediğinizden emin misiniz?')) return;
    try {
      await deleteSliderImage(id);
      toast.success('Slider silindi');
      fetchSliders();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const toggleActive = async (slider) => {
    try {
      await updateSliderImage(slider.id, {
        ...slider,
        is_active: !slider.is_active
      });
      toast.success(slider.is_active ? 'Slider gizlendi' : 'Slider aktif edildi');
      fetchSliders();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const resetForm = () => {
    setEditingSlider(null);
    setFormData({
      image_url: '',
      title: '',
      subtitle: '',
      order: 0,
      is_active: true
    });
  };

  if (loading) return <div className="text-center py-12">Yükleniyor...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Slider Yönetimi</h2>
          <p className="text-gray-600 text-sm mt-1">Ana sayfa banner görsellerini yönetin</p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus size={20} className="mr-2" /> Yeni Slider Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingSlider ? 'Slider Düzenle' : 'Yeni Slider Ekle'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Görsel *</Label>
                <div className="space-y-3 mt-1">
                  <div className="flex gap-3">
                    <Input 
                      name="image_url" 
                      value={formData.image_url} 
                      onChange={handleInputChange} 
                      placeholder="Görsel URL veya yükle"
                      className="flex-1"
                      required
                    />
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <Button type="button" variant="outline" className="h-10" disabled={uploading} asChild>
                        <span>
                          {uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
                          {uploading ? 'Yükleniyor...' : 'Yükle'}
                        </span>
                      </Button>
                    </label>
                  </div>
                  {formData.image_url && (
                    <img src={formData.image_url} alt="Önizleme" className="h-40 w-full object-cover rounded-lg border" />
                  )}
                </div>
              </div>
              <div>
                <Label>Başlık *</Label>
                <Input 
                  name="title" 
                  value={formData.title} 
                  onChange={handleInputChange} 
                  placeholder="Ana başlık"
                  required
                />
              </div>
              <div>
                <Label>Alt Başlık *</Label>
                <Input 
                  name="subtitle" 
                  value={formData.subtitle} 
                  onChange={handleInputChange} 
                  placeholder="Alt açıklama"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sıra</Label>
                  <Input 
                    name="order" 
                    type="number"
                    value={formData.order} 
                    onChange={handleInputChange} 
                    min="0"
                  />
                </div>
                <div className="flex items-center space-x-2 pt-6">
                  <input 
                    type="checkbox" 
                    name="is_active" 
                    checked={formData.is_active} 
                    onChange={handleInputChange} 
                    className="w-4 h-4"
                  />
                  <Label>Aktif (Ana sayfada göster)</Label>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">
                  {editingSlider ? 'Güncelle' : 'Ekle'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setIsModalOpen(false); resetForm(); }}>
                  İptal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Slider List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sliders.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500 bg-white rounded-2xl border">
            Henüz slider görseli bulunmuyor. Yeni bir görsel ekleyin!
          </div>
        ) : (
          sliders.map((slider) => (
            <div key={slider.id} className={`bg-white rounded-2xl border overflow-hidden ${!slider.is_active ? 'opacity-60' : ''}`}>
              <div className="relative">
                <img src={slider.image_url} alt={slider.title} className="w-full h-40 object-cover" />
                <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                  <GripVertical size={14} />
                  Sıra: {slider.order}
                </div>
                <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold ${slider.is_active ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}>
                  {slider.is_active ? 'Aktif' : 'Gizli'}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-lg text-gray-900 mb-1">{slider.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{slider.subtitle}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => toggleActive(slider)} className="flex-1">
                    {slider.is_active ? <EyeOff size={16} className="mr-1" /> : <Eye size={16} className="mr-1" />}
                    {slider.is_active ? 'Gizle' : 'Göster'}
                  </Button>
                  <Button size="sm" onClick={() => handleEdit(slider)} className="bg-blue-500 hover:bg-blue-600">
                    <Edit size={16} />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(slider.id)}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
